import { useState, useCallback, useEffect } from "react";
import {
  useSignMessage,
  useConnection,
  useDisconnect,
  type Connector,
} from "wagmi";
import { signMessage, watchConnection } from "@wagmi/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiGetUserInfo, ApiLogin } from "@/api";
import { showError, showLoading, dismissToast, showSuccess } from "@/lib/toast";
import { getToken, clearToken } from "@/lib/auth";
import { useUserStore } from "@/store/useUserStore";
import { getSavedInviteCode, clearInviteCode } from "@/lib/invite";
import { getClientInfo } from "@/lib/clientInfo";
import { finishAuthSession } from "@/lib/finishAuthSession";
import { wagmiConfig } from "@/config";
import {
  registerLogoutHandler,
  unregisterLogoutHandler,
} from "@/lib/logoutRegistry";

const SIGN_MESSAGE = "Welcome to TradingSkill";

interface UseLoginOptions {
  onSuccess?: () => void;
  onNeedInviteCode?: () => void;
}

export const useLogin = (options?: UseLoginOptions) => {
  const { t } = useTranslation();
  const { address, connector } = useConnection();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { clearUserInfo, userInfo } = useUserStore();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [needInviteCode, setNeedInviteCode] = useState(false);

  const logout = useCallback(
    (opts?: { silent?: boolean }) => {
      const authType = useUserStore.getState().userInfo?.authType;
      if (authType === "wallet") {
        disconnect?.();
      }
      clearToken();
      clearUserInfo();
      queryClient.clear();

      if (!opts?.silent) {
        showSuccess(t("toast.login.logoutSuccess"));
      }
    },
    [disconnect, clearUserInfo, queryClient, t]
  );

  const login = useCallback(
    async (
      inviteCode: string = "",
      overrides?: { address?: `0x${string}`; connector?: Connector }
    ) => {
      const targetAddress = overrides?.address || address;
      const targetConnector = overrides?.connector || connector;

      if (!targetAddress) {
        showError(t("toast.common.connectWalletFirst"));
        return false;
      }

      if (!targetConnector) {
        showError(t("toast.login.connectorNotReady"));
        return false;
      }

      setIsLoading(true);
      try {
        showLoading(t("toast.login.signing"), "sign");

        let signature: string;
        if (overrides?.connector) {
          signature = await signMessage(wagmiConfig, {
            connector: targetConnector,
            message: SIGN_MESSAGE,
          });
        } else {
          signature = await signMessageAsync({ message: SIGN_MESSAGE });
        }

        dismissToast("sign");

        const clientInfo = getClientInfo();
        const finalInviteCode = inviteCode || getSavedInviteCode() || "";

        showLoading(t("toast.login.logging"), "login");
        const response = await ApiLogin({
          address: targetAddress,
          signature,
          message: SIGN_MESSAGE,
          parentInviteCode: finalInviteCode,
          ...clientInfo,
        });

        dismissToast("login");

        const data = response.data.data;

        if (data.result === 0) {
          await finishAuthSession({
            token: response.data.token,
            email: null,
            queryClient,
          });

          showSuccess(t("toast.login.success"));
          options?.onSuccess?.();
          setNeedInviteCode(false);
          return true;
        }

        if (data.result === 2 || data.result === 3) {
          if (!finalInviteCode) {
            setNeedInviteCode(true);
            options?.onNeedInviteCode?.();
            showError(t("toast.login.needInviteCode"));
            return false;
          }
          showError(t("toast.login.invalidInviteCode"));
          clearInviteCode();
          setNeedInviteCode(true);
          return false;
        }

        if (data.result === 1) {
          showError(t("toast.login.signatureFailed"));
          return false;
        }

        showError(t("toast.login.loginFailed"));
        return false;
      } catch (error: unknown) {
        dismissToast("sign");
        dismissToast("login");
        console.error("登录失败:", error);

        const message = error instanceof Error ? error.message : String(error);
        if (
          message.includes("User rejected") ||
          message.includes("User denied")
        ) {
          showError(t("toast.login.userCancelled"));
        } else if (
          message.includes("connector") ||
          message.includes("getChainId")
        ) {
          showError(t("toast.login.connectorNotReadyRetry"));
        } else {
          showError(message || t("toast.login.loginFailed"));
        }
        logout({ silent: true });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [address, signMessageAsync, options, connector, queryClient, logout, t]
  );

  useEffect(() => {
    registerLogoutHandler(logout);
    return () => unregisterLogoutHandler(logout);
  }, [logout]);

  useEffect(() => {
    const unwatch = watchConnection(wagmiConfig, {
      onChange(connection, prevConnection) {
        const authType = useUserStore.getState().userInfo?.authType;

        if (connection.isDisconnected) {
          if (authType === "wallet") {
            logout({ silent: true });
          }
          return;
        }

        if (
          connection.isConnected &&
          connection.address !== prevConnection?.address
        ) {
          if (getToken() && authType === "email") {
            return;
          }

          if (
            getToken() &&
            userInfo?.address?.toLowerCase() !==
              connection.address?.toLowerCase()
          ) {
            logout({ silent: true });
            showError(t("toast.login.walletSwitched"));
            return;
          }

          if (!getToken()) {
            login("", {
              address: connection.address,
              connector: connection.connector,
            });
          }
        }
      },
    });
    return () => unwatch();
  }, [login, logout, t, userInfo?.address]);

  useEffect(() => {
    if (userInfo?.authType !== "wallet" || !getToken() || !address) return;

    const syncWalletUser = async () => {
      const userInfoResponse = await ApiGetUserInfo();
      if (!userInfoResponse.data) return;
      if (
        userInfoResponse.data.address?.toLowerCase() !== address.toLowerCase()
      ) {
        logout({ silent: true });
      }
    };

    syncWalletUser();
  }, [address, userInfo?.authType, logout]);

  return {
    login,
    logout,
    isLoading,
    needInviteCode,
    setNeedInviteCode,
    isLoggedIn: !!userInfo?.uid,
  };
};
